import { TraceCode, IngredientTraceCode } from '../types/traceCode';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

const traceCodeApiService = {
  /**
   * Fetch all trace codes
   */
  fetchTraceCodes: async (): Promise<TraceCode[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-trace-codes`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch trace codes');
      }

      return data.traceCodes;
    } catch (error) {
      console.error('Error fetching trace codes from API:', error);
      throw error;
    }
  },

  /**
   * Fetch ingredient trace codes for a specific batch
   */
  fetchIngredientTraceCodesByBatch: async (batchId: string): Promise<IngredientTraceCode[]> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/finished-product-trace-codes/batch/${batchId}/ingredients`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        console.error('Error response:', response.status, text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch ingredient trace codes');
      }

      return data.ingredientTraceCodes;
    } catch (error) {
      console.error('Error fetching ingredient trace codes from API:', error);
      throw error;
    }
  },

  /**
   * Create a new trace code
   */
  createTraceCode: async (traceCode: TraceCode): Promise<TraceCode> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-trace-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(traceCode),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create trace code');
      }

      return data.traceCode;
    } catch (error) {
      console.error('Error creating trace code via API:', error);
      throw error;
    }
  },

  /**
   * Update an existing trace code
   */
  updateTraceCode: async (traceCode: TraceCode): Promise<TraceCode> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/finished-product-trace-codes/${traceCode.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(traceCode),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update trace code');
      }

      return data.traceCode;
    } catch (error) {
      console.error('Error updating trace code via API:', error);
      throw error;
    }
  },

  /**
   * Delete a trace code
   */
  deleteTraceCode: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/finished-product-trace-codes/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete trace code');
      }
    } catch (error) {
      console.error('Error deleting trace code via API:', error);
      throw error;
    }
  },
};

export default traceCodeApiService;
